import { apiQueryAdapter } from "../apiQueryAdapter";
import { PageResponseModel } from "../common/models";
import {
  ZipRoutingProfile,
  ZipRoutingProfileActivityLogFilters,
  ZipRoutingProfileActivityLogModel,
  ZipRoutingProfileFormModel,
  ZipRoutingProfileListParams,
  serviceDayToPayload,
} from "./models";

const DEFAULT_PAGE_SIZE = 50;

class ZipRoutingProfileService {
  private static instance: ZipRoutingProfileService;

  private constructor() {}

  static getInstance(): ZipRoutingProfileService {
    if (!ZipRoutingProfileService.instance) {
      ZipRoutingProfileService.instance = new ZipRoutingProfileService();
    }
    return ZipRoutingProfileService.instance;
  }

  public getList = async (
    filters: ZipRoutingProfileListParams = {}
  ): Promise<PageResponseModel<ZipRoutingProfile>> => {
    const params = new URLSearchParams();

    if (filters.search?.trim()) params.set("search", filters.search.trim());
    if (filters.state?.trim()) params.set("state", filters.state.trim());
    if (filters.terminalNumber?.trim()) {
      params.set("terminalNumber", filters.terminalNumber.trim());
    }
    if (filters.direct === "true" || filters.direct === "false") {
      params.set("direct", filters.direct);
    }
    if (filters.page !== undefined) params.set("page", String(filters.page));
    params.set("size", String(filters.size ?? DEFAULT_PAGE_SIZE));

    const response = await apiQueryAdapter.query("/zip-routing-profiles", "GET", {
      params,
    });
    const pageData = response.data as PageResponseModel<ZipRoutingProfile>;
    return {
      ...pageData,
      content: (pageData?.content || []).map(this.normalizeProfileRow),
    };
  };

  public getById = async (id: string): Promise<ZipRoutingProfile> => {
    if (!id) throw new Error("Profile ID is required");
    const response = await apiQueryAdapter.query(`/zip-routing-profiles/${id}`, "GET");
    return this.normalizeProfileRow(response.data);
  };

  public create = async (
    data: ZipRoutingProfileFormModel
  ): Promise<ZipRoutingProfile> => {
    if (!data) throw new Error("Data is required");
    const response = await apiQueryAdapter.query<ZipRoutingProfile>(
      "/zip-routing-profiles",
      "POST",
      { body: this.toPayload(data) }
    );
    return response.data;
  };

  public update = async (
    id: string,
    data: ZipRoutingProfileFormModel
  ): Promise<ZipRoutingProfile> => {
    if (!id) throw new Error("Profile ID is required");
    if (!data) throw new Error("Data is required");
    const response = await apiQueryAdapter.query<ZipRoutingProfile>(
      `/zip-routing-profiles/${id}`,
      "PUT",
      { body: this.toPayload(data) }
    );
    return response.data;
  };

  public getActivityLogList = async (
    page?: number,
    filters: ZipRoutingProfileActivityLogFilters = {}
  ): Promise<PageResponseModel<ZipRoutingProfileActivityLogModel>> => {
    const params = new URLSearchParams();

    if (page !== undefined) params.set("page", String(page));
    if (filters.search?.trim()) params.set("search", filters.search.trim());
    if (filters.action) params.set("action", filters.action);
    if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.set("dateTo", filters.dateTo);

    const response = await apiQueryAdapter.query(
      "/zip-routing-profiles/activity-log/all",
      "GET",
      { params }
    );
    const pageData = response.data as PageResponseModel<ZipRoutingProfileActivityLogModel>;
    return {
      ...pageData,
      content: (pageData?.content || []).map(this.normalizeActivityLogRow),
    };
  };

  /** Normalize camelCase DTO (zip vs zipCode, Phase 1 prev/new, terminal enrichment). */
  private normalizeActivityLogRow = (
    row: ZipRoutingProfileActivityLogModel
  ): ZipRoutingProfileActivityLogModel => ({
    ...row,
    zipCode: row.zipCode ?? row.zip ?? null,
    zip: row.zip ?? row.zipCode ?? null,
    previousValue: row.previousValue ?? null,
    newValue: row.newValue ?? null,
    details: row.details ?? null,
    fieldName: row.fieldName ?? null,
    fieldLabel: row.fieldLabel ?? null,
    changeDescription: row.changeDescription ?? null,
    airportCode: row.airportCode ?? null,
    terminalName: row.terminalName ?? null,
    terminalNumber: row.terminalNumber ?? null,
  });

  /** Pass through list/detail enrichment fields; normalize zip aliases. */
  private normalizeProfileRow = (row: ZipRoutingProfile): ZipRoutingProfile => ({
    ...row,
    zipCode: row.zipCode ?? row.zip,
    zip: row.zip ?? row.zipCode,
    airportCode: row.airportCode ?? null,
    terminalName: row.terminalName ?? null,
  });

  /** Maps UI form → ZipRoutingProfileRequest body (no city) */
  private toPayload = (data: ZipRoutingProfileFormModel) => {
    const notServiced = data.coverageStatus === "NOT_SERVICED";
    return {
      zip: data.zipCode?.trim(),
      state: data.state?.trim(),
      terminalNumber: notServiced ? null : data.terminalNumber,
      coverageStatus: data.coverageStatus,
      rateType: data.direct ? "D" : "I",
      serviceMon: serviceDayToPayload(data.monday),
      serviceTue: serviceDayToPayload(data.tuesday),
      serviceWed: serviceDayToPayload(data.wednesday),
      serviceThu: serviceDayToPayload(data.thursday),
      serviceFri: serviceDayToPayload(data.friday),
      serviceSat: serviceDayToPayload(data.saturday),
      serviceSun: serviceDayToPayload(data.sunday),
      quoteAllowed: !!data.quoteAllowed,
      threePlOriginCode: data.threePlOriginCode?.trim() || null,
      threePlDestCode: data.threePlDestCode?.trim() || null,
      extensionCode1: data.extensionCode1?.trim() || null,
      extensionCode2: data.extensionCode2?.trim() || null,
    };
  };
}

export default ZipRoutingProfileService;
